import { ViewController } from 'elc-service';
import { emit, events } from 'elc-service-bus';
import * as PowerReviewsConstants from '../constants/PowerReviews';
import { IReviewsPayload } from '../interfaces/IReviews';
import { SERVICE_REVIEWS_CLASS } from '../constants/ServiceReviews';

export interface IEventHandlerType {
    id: string;
    name: string;
    reviewCategory: string;
}

export class RatingsReviewsBaseController extends ViewController {
    private productReviewsContainer: HTMLElement | null;
    public eventHandlerData: IEventHandlerType;

    public addReviewsEventHandlers(eventHandlerData: IEventHandlerType) {
        this.eventHandlerData = eventHandlerData;
        this.productReviewsContainer = <HTMLElement>(
            document.querySelector(
                `.${PowerReviewsConstants.PRODUCT_REVIEWS_CLASS}, .${SERVICE_REVIEWS_CLASS}`
            )
        );
        if (!this.productReviewsContainer) {
            return;
        }
        this.productReviewsContainer.addEventListener('click', this.handleReviewsClickEventHandler);
        this.productReviewsContainer.addEventListener(
            'change',
            this.handleReviewsChangeEventHandler
        );
        this.productReviewsContainer.addEventListener('keyup', this.handleReviewsKeyupEventHandler);
    }

    private handleReviewsClickEventHandler = (event: MouseEvent) => {
        const { id, name, reviewCategory } = this.eventHandlerData;
        const eventTarget = <HTMLElement>event.target;

        const payload: IReviewsPayload = {
            id,
            name,
            reviewCategory
        };

        const selector = Object.keys(PowerReviewsConstants.ReviewClickEventSelectors).find(key => {
            return !!eventTarget.closest(
                PowerReviewsConstants.ReviewClickEventSelectors[
                    key as keyof typeof PowerReviewsConstants.ReviewClickEventSelectors
                ]
            );
        });
        if (selector) {
            this.handleReviewClickEvents[
                PowerReviewsConstants.ReviewClickEventSelectors[
                    selector as keyof typeof PowerReviewsConstants.ReviewClickEventSelectors
                ]
            ]({ eventTarget, payload });
        }
    };

    private handleReviewClickEvents: {
        [key in PowerReviewsConstants.ReviewClickEventSelectors]: (params: {
            eventTarget: HTMLElement;
            payload: IReviewsPayload;
        }) => void;
    } = {
        [PowerReviewsConstants.ReviewClickEventSelectors.WRITE_REVIEW]: params => {
            emit(events.WRITE_REVIEW_STARTED, params.payload);
        },
        [PowerReviewsConstants.ReviewClickEventSelectors.REVIEW_HELPFUL_YES]: params => {
            emit(events.REVIEWS_HELPFUL_YES_CLICKED, params.payload);
        },
        [PowerReviewsConstants.ReviewClickEventSelectors.REVIEW_HELPFUL_NO]: params => {
            emit(events.REVIEWS_HELPFUL_NO_CLICKED, params.payload);
        },
        [PowerReviewsConstants.ReviewClickEventSelectors.FLAG_REVIEW]: params => {
            emit(events.REVIEWS_FLAG_REVIEW_CLICKED, params.payload);
        },
        [PowerReviewsConstants.ReviewClickEventSelectors.MULTI_SELECT]: params => {
            this.handleMultiSelectItem(params.eventTarget);
        },
        [PowerReviewsConstants.ReviewClickEventSelectors.REVIEW_SEARCH]: params => {
            this.handleReviewsSearch(params.eventTarget);
        },
        [PowerReviewsConstants.ReviewClickEventSelectors.ASK_AND_ANSWER_BUTTON]: params => {
            this.handleAskAndAnswerButton(params.payload);
        },
        [PowerReviewsConstants.ReviewClickEventSelectors.ASK_AND_ANSWER_SEARCH]: params => {
            this.handleAskAndAnswerSearch(params.eventTarget);
        },
        [PowerReviewsConstants.ReviewClickEventSelectors.ASK_AND_ANSWER_HELPFUL_YES]: params => {
            emit(events.ASK_AND_ANSWER_HELPFUL_YES_CLICKED, params.payload);
        },
        [PowerReviewsConstants.ReviewClickEventSelectors.ASK_AND_ANSWER_HELPFUL_NO]: params => {
            emit(events.ASK_AND_ANSWER_HELPFUL_NO_CLICKED, params.payload);
        },
        [PowerReviewsConstants.ReviewClickEventSelectors.ASK_AND_ANSWER_SHOW_MORE]: params => {
            emit(events.ASK_AND_ANSWER_SHOW_MORE_CLICKED, params.payload);
        }
    };

    private handleAskAndAnswerButton = (payload: IReviewsPayload) => {
        if (!document.getElementById(PowerReviewsConstants.ASK_AND_ANSWER_FORM_ID)) {
            emit(events.ASK_AND_ANSWER_CLICKED, payload);
        }
    };

    private handleReviewsChangeEventHandler = (event: Event) => {
        const eventTarget = <HTMLElement>event.target;
        const { id, name, reviewCategory } = this.eventHandlerData;
        const { SORT_BY_ID, ASK_AND_ANSWER_SORT_BY_ID } = PowerReviewsConstants;
        const payload: IReviewsPayload = {
            id,
            name,
            reviewCategory,
            selectedValue: (<HTMLInputElement>eventTarget).value
        };
        switch (eventTarget && eventTarget.id) {
            case SORT_BY_ID: {
                emit(events.REVIEWS_SORT_BY_CHANGED, payload);
                break;
            }
            case ASK_AND_ANSWER_SORT_BY_ID: {
                emit(events.ASK_AND_ANSWERS_SORT_BY_CHANGED, payload);
                break;
            }
            default:
        }
    };

    private handleReviewsKeyupEventHandler = (event: KeyboardEvent) => {
        const eventTarget = <HTMLElement>event.target;
        const {
            REVIEWS_SEARCH_CLASS,
            ASK_AND_ANSWER_SEARCH_CLASS,
            ENTER_KEY,
            ENTER_KEY_CODE
        } = PowerReviewsConstants;
        const isEnterKey = event.code === ENTER_KEY_CODE || event.key === ENTER_KEY;

        if (!!eventTarget.closest(`.${REVIEWS_SEARCH_CLASS}`) && isEnterKey) {
            this.handleReviewsSearch(eventTarget);
        } else if (!!eventTarget.closest(`.${ASK_AND_ANSWER_SEARCH_CLASS}`) && isEnterKey) {
            this.handleAskAndAnswerSearch(eventTarget);
        }
    };

    private handleMultiSelectItem(eventTarget: HTMLElement) {
        const { MULTI_SELECT_CLASS, MULTI_SELECT_ITEM_CLASS } = PowerReviewsConstants;
        const multiSelectParent = eventTarget && eventTarget.closest(`.${MULTI_SELECT_CLASS}`);
        const currentItem = eventTarget && eventTarget.closest(`.${MULTI_SELECT_ITEM_CLASS}`);
        const { id, name, reviewCategory } = this.eventHandlerData;

        if (currentItem && currentItem.hasAttribute('data-selected')) {
            currentItem.removeAttribute('data-selected');

            return;
        }
        currentItem && currentItem.setAttribute('data-selected', '');

        const selectedFlags = this.getMultiSelectedItems(multiSelectParent);
        const multiSelectButtonText = this.getMultiSelectButtonText(multiSelectParent);

        if (selectedFlags.length > 0) {
            const payload: IReviewsPayload = {
                id,
                name,
                reviewCategory,
                selectedValue: `${multiSelectButtonText} | ${selectedFlags.join(',')}`
            };
            emit(events.REVIEWS_FILTER_CLICKED, payload);
        }
    }

    private getMultiSelectButtonText(multiSelectParent: Element | null) {
        const multiSelectButton =
            multiSelectParent &&
            multiSelectParent.querySelector(
                `.${PowerReviewsConstants.MULTI_SELECT_BUTTON_LABEL_CLASS}`
            );

        return multiSelectButton && multiSelectButton.textContent;
    }

    private getMultiSelectedItems(multiSelectParent: Element | null) {
        const multiSelectItems =
            multiSelectParent &&
            multiSelectParent.querySelectorAll(`.${PowerReviewsConstants.MULTI_SELECT_ITEM_CLASS}`);
        const selectedFlags: string[] = [];
        multiSelectItems &&
            multiSelectItems.forEach(selectItem => {
                if (selectItem.hasAttribute('data-selected')) {
                    const selectedFlagContainer = selectItem && selectItem.querySelector('span');
                    const selectedFlagText =
                        selectedFlagContainer && selectedFlagContainer.textContent;
                    selectedFlagText && selectedFlags.push(selectedFlagText);
                }
            });

        return selectedFlags;
    }

    private handleReviewsSearch(eventTarget: HTMLElement) {
        const searchParent =
            eventTarget && eventTarget.closest(`.${PowerReviewsConstants.REVIEWS_SEARCH_CLASS}`);
        const searchBox = searchParent && searchParent.querySelector('input');
        const searchValue = searchBox && searchBox.value;
        const { id, name, reviewCategory } = this.eventHandlerData;
        if (!searchValue) {
            return;
        }
        const payload: IReviewsPayload = {
            id,
            name,
            reviewCategory,
            selectedValue: searchValue
        };
        emit(events.REVIEWS_SEARCH_CLICKED, payload);
    }

    private handleAskAndAnswerSearch(eventTarget: HTMLElement) {
        const searchParent =
            eventTarget &&
            eventTarget.closest(`.${PowerReviewsConstants.ASK_AND_ANSWER_SEARCH_CLASS}`);
        const searchBox = searchParent && searchParent.querySelector('input');
        const searchValue = searchBox && searchBox.value;
        const { id, name, reviewCategory } = this.eventHandlerData;
        if (!searchValue) {
            return;
        }
        const payload: IReviewsPayload = {
            id,
            name,
            reviewCategory,
            selectedValue: searchValue
        };
        emit(events.ASK_AND_ANSWER_SEARCH_CLICKED, payload);
    }

    public removeReviewsEventHandlers() {
        if (!this.productReviewsContainer) {
            return;
        }
        this.productReviewsContainer.removeEventListener(
            'click',
            this.handleReviewsClickEventHandler
        );
        this.productReviewsContainer.removeEventListener(
            'change',
            this.handleReviewsChangeEventHandler
        );
        this.productReviewsContainer.removeEventListener(
            'keyup',
            this.handleReviewsKeyupEventHandler
        );
    }
}
